
import React, { useCallback, useState, useMemo, useEffect } from 'react';
// Fix: Use named imports for ReactFlow hooks from reactflow.
import {
  useNodesState,
  useEdgesState,
  useReactFlow,
  Edge,
  Node
} from 'reactflow';
import { getLayoutedElements } from '../utils/autoLayout';
import { useProcessManagement } from './useProcessManagement';
import { useFileOperations } from './useFileOperations';
import { useNodeOperations } from './useNodeOperations';
import { useEdgeOperations } from './useEdgeOperations';
import { useAutoSave, AutoSavePayload } from './useAutoSave';
import { useValidation } from './useValidation';
import { AppNode, ProcessDefinition, WorkflowProject, WorkflowNodeData, Swimlane } from '../types';
import { DEFAULT_LANE_WIDTH, DEFAULT_LANE_HEIGHT, DEFAULT_LANE_COLOR } from '../components/molecules/Swimlanes';
import { getAvailableVariables } from '../utils/variableExtraction';
import { generateWorkflowProject } from '../utils/pssHelper';

interface UseWorkflowEditorProps {
    initialData?: ProcessDefinition[];
    onAutoSave?: (payload: AutoSavePayload) => void;
}

// Legacy save files use PascalCase fields (Nodes/Connections) instead of
// camelCase. Accept both during initial hydration and normalise downstream.
type LegacyProcessDefinition = ProcessDefinition & {
  Nodes?: AppNode[];
  Connections?: Edge[];
};

// Minimal node shape we rely on during pre-hydration measurement — avoids
// tying this code to ReactFlow's internal Node<T> generic before state is set up.
interface MeasurableNode {
  id: string;
  type?: string;
  position?: { x?: number; y?: number };
  parentNode?: string;
}

const SINGLE_LANE_PADDING_X = 80;
const SINGLE_LANE_PADDING_Y = 60;
const SINGLE_LANE_NODE_WIDTH = 220;
const SINGLE_LANE_NODE_HEIGHT = 100;
const SINGLE_LANE_BOUNDARY_SIZE = 56;
const SINGLE_LANE_BOUNDARY_LABEL_HEIGHT = 20;

const estimateInitialNodeSize = (node: MeasurableNode) => {
  if (node?.type?.endsWith('BoundaryEvent')) {
    return {
      width: SINGLE_LANE_BOUNDARY_SIZE,
      height: SINGLE_LANE_BOUNDARY_SIZE + SINGLE_LANE_BOUNDARY_LABEL_HEIGHT,
    };
  }

  return {
    width: SINGLE_LANE_NODE_WIDTH,
    height: SINGLE_LANE_NODE_HEIGHT,
  };
};

const inferLaneBoundsFromNodes = (nodes: MeasurableNode[]) => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return {
      x: 0,
      y: 0,
      width: DEFAULT_LANE_WIDTH,
      height: DEFAULT_LANE_HEIGHT,
    };
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const absolutePositionCache = new Map<string, { x: number; y: number }>();

  const getAbsolutePosition = (node: MeasurableNode): { x: number; y: number } => {
    const cached = absolutePositionCache.get(node.id);
    if (cached) return cached;

    const own = {
      x: Number(node?.position?.x) || 0,
      y: Number(node?.position?.y) || 0,
    };

    if (!node?.parentNode) {
      absolutePositionCache.set(node.id, own);
      return own;
    }

    const parent = nodeById.get(node.parentNode);
    if (!parent) {
      absolutePositionCache.set(node.id, own);
      return own;
    }

    const parentAbs = getAbsolutePosition(parent);
    const absolute = {
      x: parentAbs.x + own.x,
      y: parentAbs.y + own.y,
    };
    absolutePositionCache.set(node.id, absolute);
    return absolute;
  };

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  nodes.forEach((node) => {
    const position = getAbsolutePosition(node);
    const size = estimateInitialNodeSize(node);
    minX = Math.min(minX, position.x);
    minY = Math.min(minY, position.y);
    maxX = Math.max(maxX, position.x + size.width);
    maxY = Math.max(maxY, position.y + size.height);
  });

  return {
    x: minX - SINGLE_LANE_PADDING_X,
    y: minY - SINGLE_LANE_PADDING_Y,
    width: Math.max(DEFAULT_LANE_WIDTH, (maxX - minX) + SINGLE_LANE_PADDING_X * 2),
    height: Math.max(DEFAULT_LANE_HEIGHT, (maxY - minY) + SINGLE_LANE_PADDING_Y * 2),
  };
};

export const useWorkflowEditor = ({ initialData, onAutoSave }: UseWorkflowEditorProps = {}) => {
  // Determine start state from props or default (Empty Shell if none provided)
  const startingProcess: ProcessDefinition = (initialData && initialData.length > 0)
      ? initialData[0]
      : { id: 'proc_1', name: 'New Process', nodes: [], edges: [] };

  // Deep Clone initial state to avoid reference issues. Legacy save files may
  // use PascalCase fields (Nodes/Connections) — reflect both shapes in the type.
  // startingProcess is unconditionally assigned above, so no fallback is needed.
  const initialProcessClone: LegacyProcessDefinition = JSON.parse(JSON.stringify(startingProcess));

  const clonedNodes: AppNode[] = initialProcessClone.nodes || initialProcessClone.Nodes || [];
  const clonedSwimlanes = initialProcessClone.swimlanes;
  const actorNames = new Set<string>();
  clonedNodes.forEach((node) => {
    const actorType = node?.data?.actorType;
    if (typeof actorType === 'string' && actorType.trim()) {
      actorNames.add(actorType.trim());
    }
  });
  if (Array.isArray(clonedSwimlanes)) {
    clonedSwimlanes.forEach((lane) => {
      if (typeof lane?.name === 'string' && lane.name.trim()) {
        actorNames.add(lane.name.trim());
      }
    });
  }

  if (actorNames.size === 1) {
    const [actorName] = Array.from(actorNames);
    initialProcessClone.nodes = clonedNodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        actorType: actorName,
      },
    }));

    const bounds = inferLaneBoundsFromNodes(initialProcessClone.nodes as MeasurableNode[]);
    if (Array.isArray(clonedSwimlanes) && clonedSwimlanes.length === 1) {
      const [lane] = clonedSwimlanes;
      const hasExplicitGeometry =
        Number.isFinite(Number(lane?.x)) &&
        Number.isFinite(Number(lane?.y)) &&
        Number.isFinite(Number(lane?.width)) &&
        Number.isFinite(Number(lane?.height));

      initialProcessClone.swimlanes = [{
        ...lane,
        name: actorName,
        x: hasExplicitGeometry ? Number(lane.x) : bounds.x,
        y: hasExplicitGeometry ? Number(lane.y) : bounds.y,
        width: hasExplicitGeometry ? Number(lane.width) : bounds.width,
        height: hasExplicitGeometry ? Number(lane.height) : bounds.height,
        color: lane?.color || DEFAULT_LANE_COLOR,
      }];
    } else {
      initialProcessClone.swimlanes = [{
        id: `lane_${Date.now()}_0`,
        name: actorName,
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height,
        color: DEFAULT_LANE_COLOR,
      }];
    }
  } else if (actorNames.size === 0 && !Array.isArray(clonedSwimlanes)) {
    initialProcessClone.swimlanes = [];
  }

  const { fitView, setCenter } = useReactFlow();

  const [isLoading, setIsLoading] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // React Flow State Initialization
  const initNodes: AppNode[] = initialProcessClone.nodes || initialProcessClone.Nodes || [];
  const initEdges: Edge[] = initialProcessClone.edges || initialProcessClone.Connections || [];

  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);

  // Virtual swimlanes: independent, movable, resizable rectangles. Each lane
  // corresponds to an actorType (lane.name). Source of truth, in order:
  //   1. Explicit process.swimlanes (restored from Editor.Swimlanes extension)
  //   2. Derived from existing node actorTypes as a fallback (legacy projects)
  const [swimlanes, setSwimlanes] = useState<Swimlane[]>(() => {
    const restored = initialProcessClone.swimlanes;
    if (Array.isArray(restored) && restored.length > 0) return restored;

    const fromNodes = new Set<string>();
    initNodes.forEach((n) => {
      if (n?.data?.actorType) fromNodes.add(n.data.actorType);
    });
    if (fromNodes.size !== 1) return [];

    const [name] = Array.from(fromNodes);
    const bounds = inferLaneBoundsFromNodes(initNodes as MeasurableNode[]);
    return [{
      id: `lane_${Date.now()}_0`,
      name,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      color: DEFAULT_LANE_COLOR,
    }];
  });

  const [selectedSwimlaneId, setSelectedSwimlaneId] = useState<string | null>(null);

  const addSwimlane = useCallback((position?: { x: number; y: number }) => {
    setSwimlanes(prev => {
      const existing = new Set(prev.map(l => l.name));
      let i = prev.length + 1;
      let name = `Actor ${i}`;
      while (existing.has(name)) { i++; name = `Actor ${i}`; }
      const pos = position ?? {
        x: 40,
        y: prev.length > 0
          ? Math.max(...prev.map(l => l.y + l.height)) + 20
          : 40,
      };
      const lane: Swimlane = {
        id: `lane_${Date.now()}`,
        name,
        x: pos.x,
        y: pos.y,
        width: DEFAULT_LANE_WIDTH,
        height: DEFAULT_LANE_HEIGHT,
        color: DEFAULT_LANE_COLOR,
      };
      return [...prev, lane];
    });
  }, []);

  const renameSwimlane = useCallback((id: string, newName: string) => {
    setSwimlanes(prev => {
      const lane = prev.find(l => l.id === id);
      if (!lane) return prev;
      const oldName = lane.name;
      if (oldName === newName) return prev;
      // Propagate rename to nodes.
      setNodes(nds => nds.map(n =>
        n.data.actorType === oldName
          ? { ...n, data: { ...n.data, actorType: newName } }
          : n
      ));
      return prev.map(l => l.id === id ? { ...l, name: newName } : l);
    });
  }, [setNodes]);

  // Delta-based move: callers pass the *incremental* (dx, dy) applied since
  // the previous tick. This lets us update lane and member nodes by exactly
  // the same amount WITHOUT nesting setNodes inside a setSwimlanes updater
  // — which in React Strict Mode would fire the nested dispatch twice and
  // double the node translation.
  const moveSwimlaneBy = useCallback((id: string, dx: number, dy: number) => {
    if (dx === 0 && dy === 0) return;
    const lane = swimlanes.find(l => l.id === id);
    if (!lane) return;
    const actorName = lane.name;
    setSwimlanes(prev => prev.map(l => l.id === id ? { ...l, x: l.x + dx, y: l.y + dy } : l));
    setNodes(nds => nds.map(n => {
      if (n.data.actorType !== actorName) return n;
      if (n.parentNode) return n;
      return { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } };
    }));
  }, [swimlanes, setNodes]);

  // Absolute-position setter (kept for programmatic / auto-layout callers).
  const moveSwimlane = useCallback((id: string, x: number, y: number) => {
    const lane = swimlanes.find(l => l.id === id);
    if (!lane) return;
    moveSwimlaneBy(id, x - lane.x, y - lane.y);
  }, [swimlanes, moveSwimlaneBy]);

  const resizeSwimlane = useCallback((id: string, width: number, height: number) => {
    setSwimlanes(prev => prev.map(l => l.id === id ? { ...l, width, height } : l));
  }, []);

  const setSwimlaneColor = useCallback((id: string, color: string) => {
    setSwimlanes(prev => prev.map(l => l.id === id ? { ...l, color } : l));
  }, []);

  const removeSwimlane = useCallback((id: string) => {
    setSwimlanes(prev => {
      const removed = prev.find(l => l.id === id);
      if (removed) {
        setNodes(nds => nds.map(n => {
          if (n.data.actorType !== removed.name) return n;
          const { actorType, ...rest } = n.data;
          return { ...n, data: rest as WorkflowNodeData };
        }));
      }
      return prev.filter(l => l.id !== id);
    });
    setSelectedSwimlaneId(curr => curr === id ? null : curr);
  }, [setNodes]);

  const selectedSwimlane = useMemo(
    () => swimlanes.find(l => l.id === selectedSwimlaneId) || null,
    [swimlanes, selectedSwimlaneId]
  );

  // Delete / Backspace removes the currently selected swimlane — but only
  // when the keystroke isn't consumed by a form field (input / textarea /
  // contenteditable). ReactFlow already handles Delete for nodes/edges; if
  // a swimlane is selected we take priority since lanes aren't RF nodes.
  useEffect(() => {
    if (!selectedSwimlaneId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (target && (target as HTMLElement).isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      removeSwimlane(selectedSwimlaneId);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedSwimlaneId, removeSwimlane]);

  // Sub-hooks
  const {
      processes,
      setProcesses,
      activeProcessId,
      setActiveProcessId,
      handleSelectProcess,
      handleCreateProcess,
      handleCreateSubProcess,
      handleDeleteProcess,
      handleUpdateProcess
  } = useProcessManagement(nodes, setNodes, edges, setEdges, swimlanes, setSwimlanes, initialData);

  const {
      handleSaveProject,
      handleLoadProject,
      projectName
  } = useFileOperations({
      nodes,
      edges,
      processes,
      setProcesses,
      activeProcessId,
      setActiveProcessId,
      setNodes,
      setEdges,
      setIsLoading,
      swimlanes,
      setSwimlanes,
  });

  const {
      selectedNode,
      setSelectedNode,
      isModalOpen,
      setIsModalOpen,
      activeTabId,
      setActiveTabId,
      openModalAtTab,
      onDragOver,
      onDrop,
      onNodeClick: originalOnNodeClick,
      onPaneClick: originalOnPaneClick,
      onNodeDoubleClick,
      onNodeContextMenu,
      onNodeDragStop,
      updateNodeData,
      renameNode
  } = useNodeOperations(nodes, setNodes, setEdges, swimlanes);

  const {
      onConnect,
      onConnectStart,
      onConnectEnd
  } = useEdgeOperations(nodes, setNodes, setEdges);

  // Wrapped handlers to manage mutual exclusion between Node, Edge and Swimlane
  // selection. The PropertiesPanel renders whichever of the three is set, in
  // priority swimlane > edge > node, so switching selections must clear the
  // others — otherwise the panel stays stuck on the previous selection.
  const onNodeClick = useCallback((e: React.MouseEvent, node: Node) => {
      setSelectedEdgeId(null);
      setSelectedSwimlaneId(null);
      originalOnNodeClick(e, node);
  }, [originalOnNodeClick]);

  const onEdgeClick = useCallback((_e: React.MouseEvent, edge: Edge) => {
      setSelectedNode(null);
      setSelectedSwimlaneId(null);
      setSelectedEdgeId(edge.id);
  }, [setSelectedNode]);

  const onPaneClick = useCallback(() => {
      setSelectedEdgeId(null);
      setSelectedSwimlaneId(null);
      originalOnPaneClick();
  }, [originalOnPaneClick]);

  // Select a swimlane and clear other selections.
  const selectSwimlane = useCallback((id: string | null) => {
      setSelectedSwimlaneId(id);
      if (id) {
          setSelectedEdgeId(null);
          setSelectedNode(null);
      }
  }, [setSelectedNode]);

  // React Flow's authoritative selection callback. Fires whenever the
  // internal node/edge selection changes — including cases where
  // onNodeClick is skipped (drag-ish clicks, keyboard selection, etc.).
  // Always call the setters — stale closures on selectedNode/selectedEdgeId
  // caused the sync to short-circuit previously. Primitive-ID setters no-op
  // on unchanged values, so there's no extra render cost.
  const onSelectionChange = useCallback((params: { nodes: Node[]; edges: Edge[] }) => {
      const selNodeId = params.nodes[0]?.id ?? null;
      const selEdgeId = params.edges[0]?.id ?? null;
      const node = selNodeId ? nodes.find(n => n.id === selNodeId) ?? null : null;
      setSelectedNode(node);
      setSelectedEdgeId(selEdgeId);
      // Clear swimlane selection whenever ReactFlow reports a node or edge
      // selection — keeps the three selection sources mutually exclusive and
      // prevents the PropertiesPanel from sticking on the previous selection.
      if (selNodeId || selEdgeId) setSelectedSwimlaneId(null);
  }, [nodes, setSelectedNode]);

  const selectedEdge = useMemo(() => {
      if (!selectedEdgeId) return null;
      return edges.find(e => e.id === selectedEdgeId) || null;
  }, [edges, selectedEdgeId]);

  const updateEdgeData = useCallback((id: string, newData: Partial<Edge>) => {
      setEdges(eds => eds.map(e => {
          if (e.id === id) {
              return { ...e, ...newData };
          }
          return e;
      }));
  }, [setEdges]);

  // Auto-Save Hook with callback support
  const autoSaveState = useAutoSave(processes, activeProcessId, nodes, edges, onAutoSave);

  // Live validation — debounced re-run on node/edge/process changes.
  const validation = useValidation({
    processes,
    activeProcessId,
    nodes,
    edges,
    swimlanes,
  });

  // --- Variable Context Calculation ---
  const variableContext = useMemo(() => {
      if (!selectedNode) return [];
      return getAvailableVariables(nodes, edges, selectedNode.id);
  }, [selectedNode, nodes, edges, isModalOpen]);

  // --- Auto Layout ---
  const handleAutoLayout = useCallback(() => {
    try {
      // Normalize actorType before layout so every top-level node is
      // linked to the lane it belongs in. Otherwise the auto-layout's
      // lane-member detection (which keys on actorType === lane.name)
      // drops nodes and they land outside the lane rectangle.
      //   Single-lane case: every orphan node joins that lane.
      //   Multi-lane case:  node center decides containment; if none,
      //                     current actorType is left alone.
      const normalizedNodes: AppNode[] = nodes.map(n => {
        if (n.parentNode) return n;
        const current = n.data?.actorType;
        if (swimlanes.length === 1) {
          const only = swimlanes[0];
          if (current === only.name) return n;
          return { ...n, data: { ...n.data, actorType: only.name } };
        }
        if (swimlanes.length > 1) {
          const w = n.width ?? 220;
          const h = n.height ?? 64;
          const cx = n.position.x + w / 2;
          const cy = n.position.y + h / 2;
          const containing = swimlanes.find(l => cx >= l.x && cx <= l.x + l.width && cy >= l.y && cy <= l.y + l.height);
          if (containing && current !== containing.name) {
            return { ...n, data: { ...n.data, actorType: containing.name } };
          }
        }
        return n;
      });

      const result = getLayoutedElements(normalizedNodes, edges, 'LR', swimlanes);
      setNodes([...result.nodes]);
      setSwimlanes([...result.swimlanes]);
      setTimeout(() => fitView({ duration: 800 }), 10);
    } catch (err) {
      // Surface layout errors instead of silently no-op'ing — otherwise a
      // broken layout looks indistinguishable from 'nothing happened'. We log
      // and return so callers can observe the early exit via unchanged state.
      console.error('Auto layout failed:', err);
      return;
    }
  }, [nodes, edges, swimlanes, setNodes, fitView]);

  // Viewport helper
  const focusNode = useCallback((nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
          setCenter(node.position.x + 90, node.position.y + 32, { zoom: 1.2, duration: 800 });
      }
  }, [nodes, setCenter]);

  // Focus + select — used by Issues panel click-through. Dispatches
  // selection changes through RF's standard change pipeline so both the
  // node prop (selected=true) and RF's internal nodeInternals store stay
  // in sync.
  const focusAndSelectNode = useCallback((nodeId: string) => {
      const target = nodes.find(n => n.id === nodeId);
      if (!target) return;
      const changes = nodes.map(n => ({ id: n.id, type: 'select' as const, selected: n.id === nodeId }));
      onNodesChange(changes);
      setEdges(eds => eds.map(e => e.selected ? { ...e, selected: false } : e));
      setSelectedEdgeId(null);
      setSelectedSwimlaneId(null);
      setCenter(target.position.x + 90, target.position.y + 32, { zoom: 1.2, duration: 800 });
  }, [nodes, onNodesChange, setEdges, setCenter]);

  // Helper to get current full project state in PSS format (for external saves)
  const getProjectData = useCallback((): WorkflowProject => {
      const currentSnapshot = processes.map(p =>
          p.id === activeProcessId ? { ...p, nodes, edges, swimlanes } : p
      );
      return generateWorkflowProject(currentSnapshot, projectName);
  }, [processes, activeProcessId, nodes, edges, swimlanes, projectName]);

  return {
    nodes, setNodes, onNodesChange,
    edges, setEdges, onEdgesChange,
    processes, activeProcessId,
    selectedNode, setSelectedNode,
    selectedEdge,
    variableContext, // Export context
    isModalOpen, setIsModalOpen,
    activeTabId,
    setActiveTabId,
    openModalAtTab,
    isDeployModalOpen, setIsDeployModalOpen,
    handleSelectProcess, handleCreateProcess, handleCreateSubProcess, handleDeleteProcess, handleUpdateProcess,
    handleSaveProject, handleLoadProject,
    handleAutoLayout,
    onConnect, onConnectStart, onConnectEnd, onDrop, onDragOver,
    onNodeClick, onEdgeClick, onPaneClick, onNodeDoubleClick, onNodeContextMenu, onNodeDragStop, onSelectionChange,
    updateNodeData, updateEdgeData, renameNode, fitView, focusNode, focusAndSelectNode,
    swimlanes, addSwimlane, renameSwimlane, removeSwimlane,
    moveSwimlane, moveSwimlaneBy, resizeSwimlane, setSwimlaneColor,
    selectedSwimlane, selectedSwimlaneId, selectSwimlane,
    setIsLoading, isLoading, autoSaveState,
    getProjectData,
    validation,
  };
};
